import { SubmissionNotifications } from '@/components/SubmissionNotifications';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useForm, useFormSubmissions } from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import type { ResolvedFieldValue } from '@wordpuppi/shared';

export const Route = createFileRoute('/sites/$siteSlug/forms/$formId/submissions')({
  component: FormSubmissionsPage,
});

function FormSubmissionsPage() {
  const { siteSlug, formId } = Route.useParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const perPage = 20;

  const { data: formData } = useForm(siteSlug, formId);
  const { data, isLoading, error } = useFormSubmissions(siteSlug, formId, {
    page,
    per_page: perPage,
    search: search || undefined,
  });

  const form = formData?.data;
  const submissions = data?.data?.submissions ?? [];
  const totalPages = data?.data?.total_pages ?? 1;
  const total = data?.data?.total ?? 0;

  // Get all fields from form steps, excluding non-input fields
  const fields = useMemo(() => {
    if (!form?.steps) return [];
    const nonInputTypes = ['heading', 'paragraph'];
    return form.steps
      .flatMap((step) => step.fields)
      .filter((f) => !nonInputTypes.includes(f.field_type))
      .sort((a, b) => a.order - b.order);
  }, [form?.steps]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatFieldValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Get field value from submission data by field name
  const getFieldValue = (data: ResolvedFieldValue[], fieldName: string): string => {
    const field = data.find((f) => f.name === fieldName);
    return field ? formatFieldValue(field.value) : '-';
  };

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Failed to load submissions
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Link
              to="/sites/$siteSlug/forms"
              params={{ siteSlug }}
              className="hover:underline"
            >
              Forms
            </Link>
            <span>/</span>
            <Link
              to="/sites/$siteSlug/forms/$formId"
              params={{ siteSlug, formId }}
              className="hover:underline"
            >
              {form?.name ?? 'Form'}
            </Link>
          </div>
          <h1 className="text-2xl font-semibold">Submissions</h1>
          <p className="text-muted-foreground mt-1">
            {total} submission{total !== 1 ? 's' : ''} for this form
          </p>
        </div>
        <Button variant="secondary" asChild>
          <Link to="/sites/$siteSlug/forms/$formId" params={{ siteSlug, formId }}>
            Back to Form
          </Link>
        </Button>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search submissions..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="max-w-sm"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : submissions.length === 0 ? (
        <div className="border border-dashed rounded-lg py-16 px-6 text-center">
          <p className="text-lg font-medium">
            {search ? 'No matching submissions' : 'No submissions yet'}
          </p>
          <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
            {search
              ? 'Try a different search term.'
              : 'Submissions from this form will appear here as visitors fill it out.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">Submitted</TableHead><TableHead>Notifications</TableHead>
                  {fields.map((field) => (
                    <TableHead key={field.id}>{field.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(submission.created_at)}
                    </TableCell>
                    <TableCell><SubmissionNotifications siteSlug={siteSlug} formId={formId} submissionId={submission.id} /></TableCell>
                    {fields.map((field) => (
                      <TableCell key={field.id} className="max-w-xs truncate">
                        {getFieldValue(submission.data, field.name)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
